<template>
  <div class="access-groups-form promotion-products-section">
    <OurGroup :title="$t('promotions.products.title')">
      <template v-if="!readonly">
        <OurTabs v-model="selectedTab" class="tabs" size="lg">
          <OurTab :label="$t('promotions.products.addBySku')" :value="TAB_SKU" />
          <OurTab :label="$t('promotions.products.importExcel')" :value="TAB_EXCEL" />
          <OurTab :label="$t('promotions.products.addByCategory')" :value="TAB_CATEGORY" />
        </OurTabs>

        <AddProductsBySkuTab v-if="selectedTab === TAB_SKU" ref="skuTab" @add-sku="onAddSku" />

        <ImportProductsExcelTab
          v-else-if="selectedTab === TAB_EXCEL"
          @import-file="onImportExcelFile"
        />

        <div v-else>
          <OurButton
            :text="$t('promotions.products.selectCategory')"
            variant="thirdary"
            filled
            @click="isShownCategoryModal = true"
          />
        </div>
      </template>

      <PromotionProductsTable
        :items="products"
        :pagination-data="productsPaginationData"
        :not-found-skus="notFoundSkus"
        :duplicate-skus="duplicateSkus"
        :readonly="readonly"
        :loading="isTableLoading"
        :loading-text="categoryLoadingText"
        @delete="onDeleteProducts"
        @pageChange="onPageChange"
      />
    </OurGroup>

    <AddProductsByCategoryModal
      v-if="!readonly"
      :show.sync="isShownCategoryModal"
      @add-category="onAddCategory"
    />
  </div>
</template>

<script>
import apolloMixin from "@/mixins/apollo.mixin";
import NotifyService from "@/services/notify.service";
import AddProductsBySkuTab from "@/views/settings/promotions/add/_components/AddProductsBySkuTab.vue";
import ImportProductsExcelTab from "@/views/settings/promotions/add/_components/ImportProductsExcelTab.vue";
import PromotionProductsTable from "@/views/settings/promotions/add/_components/PromotionProductsTable.vue";
import AddProductsByCategoryModal from "@/views/settings/promotions/add/_components/AddProductsByCategoryModal.vue";
import {
  addDealerIncentiveProductsByIdsMutation,
  dealerIncentiveProductsPreviewByArticlesQuery,
  dealerIncentiveProductsPreviewByCategoryQuery,
  previewDealerIncentiveProductsFromExcelMutation,
  removeDealerIncentiveProductsMutation,
} from "@/views/settings/promotions/add/gql/products";

const TAB_SKU = "sku";
const TAB_EXCEL = "excel";
const TAB_CATEGORY = "category";
const CATEGORY_PREVIEW_PER_PAGE = 50;

export default {
  name: "PromotionProductsSection",

  components: {
    AddProductsBySkuTab,
    ImportProductsExcelTab,
    PromotionProductsTable,
    AddProductsByCategoryModal,
  },

  mixins: [apolloMixin],

  props: {
    value: {
      type: Array,
      default: () => [],
    },

    notFoundSkus: {
      type: Array,
      default: () => [],
    },

    duplicateSkus: {
      type: Array,
      default: () => [],
    },

    dealerIncentiveId: {
      type: String,
      default: "",
    },

    productsPaginationData: {
      type: Object,
      default: null,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    productsPageLoading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      TAB_SKU,
      TAB_EXCEL,
      TAB_CATEGORY,
      selectedTab: TAB_SKU,
      isShownCategoryModal: false,
      isLoadingCategoryProducts: false,
      categoryLoadingProgress: null,
    };
  },

  computed: {
    products: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },

    isEditMode() {
      return Boolean(this.dealerIncentiveId);
    },

    isServerPagination() {
      return Boolean(this.productsPaginationData);
    },

    isTableLoading() {
      return this.isLoadingCategoryProducts || this.productsPageLoading;
    },

    categoryLoadingText() {
      if (!this.isLoadingCategoryProducts) {
        return "";
      }

      if (!this.categoryLoadingProgress) {
        return this.$t("promotions.products.loadingCategory");
      }

      return this.$t("promotions.products.loadingCategoryProgress", {
        current: this.categoryLoadingProgress.current,
        total: this.categoryLoadingProgress.total,
      });
    },
  },

  methods: {
    validate() {
      return true;
    },

    getProductIds() {
      return this.products.map((product) => product.id).filter(Boolean);
    },

    mapProduct(product) {
      return {
        id: product.id,
        productCode: product.productCode || product.article,
        name: product.name,
        categoryName: product.category?.description || product.categoryName || "–",
      };
    },

    mergeProducts(newProducts = []) {
      if (!newProducts.length) {
        return [];
      }

      const existingIds = new Set(this.products.map((product) => product.id));
      const existingSkus = new Set(this.products.map((product) => product.productCode));
      const toAdd = newProducts
        .map((product) => this.mapProduct(product))
        .filter(
          (product) =>
            product.productCode &&
            !existingIds.has(product.id) &&
            !existingSkus.has(product.productCode)
        );

      if (toAdd.length) {
        this.products = [...this.products, ...toAdd];
      }

      return toAdd;
    },

    mergeNotFoundSkus(skus = []) {
      if (!skus.length) {
        return;
      }

      const merged = [...new Set([...this.notFoundSkus, ...skus])];

      this.$emit("update:notFoundSkus", merged);
    },

    mergeDuplicateSkus(skus = []) {
      if (!skus.length) {
        return;
      }

      const merged = [...new Set([...this.duplicateSkus, ...skus])];

      this.$emit("update:duplicateSkus", merged);
    },

    applyPreviewResult({ products, notFoundArticles }) {
      const added = this.mergeProducts(products || []);

      if (notFoundArticles?.length) {
        this.mergeNotFoundSkus(notFoundArticles);
      }

      return added;
    },

    applyAddResult(result) {
      if (!result) {
        return;
      }

      if (result.notFoundArticles?.length) {
        this.mergeNotFoundSkus(result.notFoundArticles);
      }

      if (result.duplicateArticles?.length) {
        this.mergeDuplicateSkus(result.duplicateArticles);
      }
    },

    async persistProducts(productIds) {
      if (!this.dealerIncentiveId || !productIds.length) {
        return;
      }

      const { addDealerIncentiveProductsByIds } = await this.$post(
        addDealerIncentiveProductsByIdsMutation,
        {
          dealerIncentiveId: this.dealerIncentiveId,
          productIds,
        },
        { withNotify: false }
      );

      this.applyAddResult(addDealerIncentiveProductsByIds);

      if (this.isServerPagination) {
        this.onPageChange({
          page: 1,
          perPage: this.productsPaginationData?.perPage || CATEGORY_PREVIEW_PER_PAGE,
        });
      }
    },

    async previewAndAddProducts(previewResult) {
      const added = this.applyPreviewResult(previewResult);

      if (this.isEditMode && added.length) {
        await this.persistProducts(added.map((product) => product.id));
      }

      return added;
    },

    async fetchAllCategoryPreviewProducts(catalogCategoryId) {
      this.isLoadingCategoryProducts = true;
      this.categoryLoadingProgress = null;

      const products = [];
      let page = 1;
      let lastPage = 1;

      try {
        do {
          this.categoryLoadingProgress = { current: page, total: lastPage };

          const { dealerIncentiveProductsPreviewByCategory } = await this.$get(
            dealerIncentiveProductsPreviewByCategoryQuery,
            {
              catalogCategoryId,
              first: CATEGORY_PREVIEW_PER_PAGE,
              page,
            },
            { withLoader: false }
          );

          const pageData = dealerIncentiveProductsPreviewByCategory;

          products.push(...(pageData?.data || []));
          lastPage = pageData?.paginatorInfo?.lastPage || 1;
          this.categoryLoadingProgress = { current: page, total: lastPage };
          page += 1;
        } while (page <= lastPage);

        return products;
      } finally {
        this.isLoadingCategoryProducts = false;
        this.categoryLoadingProgress = null;
      }
    },

    async onAddSku(sku) {
      const { dealerIncentiveProductsPreviewByArticles } = await this.$get(
        dealerIncentiveProductsPreviewByArticlesQuery,
        { articles: [sku] }
      );

      await this.previewAndAddProducts(dealerIncentiveProductsPreviewByArticles);
    },

    async onImportExcelFile(file) {
      const { previewDealerIncentiveProductsFromExcel } = await this.$post(
        previewDealerIncentiveProductsFromExcelMutation,
        { file }
      );

      await this.previewAndAddProducts(previewDealerIncentiveProductsFromExcel);
    },

    async onAddCategory(category) {
      this.isShownCategoryModal = false;

      const products = await this.fetchAllCategoryPreviewProducts(category.id);
      const added = await this.previewAndAddProducts({ products });

      if (products.length) {
        NotifyService.success("addCategoryProducts", {
          count: added.length,
          category: category.name,
        });
      }
    },

    onPageChange(payload) {
      this.$emit("pageChange", payload);
    },

    async onDeleteProducts(ids) {
      if (!ids.length) {
        return;
      }

      if (this.dealerIncentiveId) {
        await this.$post(
          removeDealerIncentiveProductsMutation,
          {
            dealerIncentiveId: this.dealerIncentiveId,
            productIds: ids,
          },
          { withNotify: false }
        );

        this.onPageChange({
          page: this.productsPaginationData.currentPage,
          perPage: this.productsPaginationData.perPage,
        });

        return;
      }

      const idsSet = new Set(ids);

      this.products = this.products.filter((product) => !idsSet.has(product.id));
    },
  },
};
</script>
